import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Label } from '@atlaskit/form';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { EditAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/custom-field/index.tsx';
import { EnvironmentLozenge } from '@atlassian/jira-forge-ui-extension-title/src/ui/environment-lozenge/index.tsx';
import type { ForgeUiAnalyticsAttributes } from '@atlassian/jira-forge-ui-types/src/common/types/analytics.tsx';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import type {
	ForgeUiIssueCreateData,
	ForgeUiIssueTransitionData,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type {
	CustomField,
	CustomFieldType,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { extractEnvFromFieldDescription } from '@atlassian/jira-forge-ui-utils/src/utils/extract-env-from-field-description/index.tsx';
import { FieldMessage } from '@atlassian/jira-issue-form-field-base/src/common/ui/field-message/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getExtensionIdFromLegacyFieldType, isForgeLegacyFieldType } from '../utils/index.tsx';
import { EditMode } from './edit/index.tsx';
import { ForgeFieldMessage } from './field-message/index.tsx';
import type { FieldProps } from './types.tsx';
import { transformValueToExtensionData } from './utils/index.tsx';

export type Props = {
	fieldType: string;
	fieldId: string;
	fieldName: string;
	description?: string | null;
	fieldProps: FieldProps<ForgeCustomFieldValue>;
	renderContext: 'issue-create' | 'issue-transition';
	experience?: 'issue-create' | 'issue-transition'; // remove optional once ditto-fcf-support-new-manifest-on-frontend is cleaned up
	issueExtensionData: ForgeUiIssueCreateData | ForgeUiIssueTransitionData;
	extension: CustomField | CustomFieldType | null;
};

export const ForgeFieldView = ({
	fieldType,
	fieldName,
	fieldId,
	fieldProps,
	issueExtensionData,
	renderContext,
	extension,
	description: rawDescription,
	experience,
}: Props): React.JSX.Element | null => {
	const { onChange, value, error } = fieldProps;

	const extensionPayload = useMemo(() => {
		if (value === undefined) {
			return {
				fieldValue: null,
			};
		}
		return { fieldValue: transformValueToExtensionData(value, extension) };
	}, [extension, value]);

	const extensionData = fg('ditto-fcf-support-new-manifest-on-frontend')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => {
				const defaultExtensionData = {
					fieldId,
					fieldType: isForgeLegacyFieldType(fieldType)
						? (getExtensionIdFromLegacyFieldType(fieldType) ?? fieldType)
						: fieldType,
					fieldName,
					renderContext,
					experience,
				};

				if (!issueExtensionData) {
					return defaultExtensionData;
				}

				return {
					...defaultExtensionData,
					...issueExtensionData,
				};
			}, [issueExtensionData, fieldId, fieldName, fieldType, renderContext, experience])
		: // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(() => {
				const defaultExtensionData = {
					fieldId,
					fieldType: isForgeLegacyFieldType(fieldType)
						? (getExtensionIdFromLegacyFieldType(fieldType) ?? fieldType)
						: fieldType,
					fieldName,
					renderContext,
				};

				if (!issueExtensionData) {
					return defaultExtensionData;
				}

				return {
					...defaultExtensionData,
					...issueExtensionData,
				};
			}, [issueExtensionData, fieldId, fieldName, fieldType, renderContext]);

	const analyticsAttributes = useMemo(
		() => ({
			renderContext,
			fieldType: extension?.properties.type,
			module: extension?.type,
			...(extension ? getAnalyticsAttributesFromExtension(extension) : {}),
		}),
		[extension, renderContext],
	);

	if (!extension) {
		return null;
	}

	const { environment, description } = extractEnvFromFieldDescription(rawDescription || '');

	const renderFieldMessage = (err?: string | boolean) => {
		return <FieldMessage description={description} error={err} fieldName={fieldName} />;
	};

	return (
		<>
			<Label htmlFor={`${fieldId}-field`}>
				<ForgeLabel>
					{fieldName} <EnvironmentLozenge environmentType={environment} />
				</ForgeLabel>
			</Label>
			<EditMode
				extension={extension}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				analyticsAttributes={analyticsAttributes}
				onChange={onChange}
			/>
			<ForgeFieldMessage error={error} fieldId={fieldId} renderFieldMessage={renderFieldMessage} />
		</>
	);
};

export const ForgeField = (props: Props): React.JSX.Element => {
	const { extension } = props;
	const analyticsAttributes = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				renderContext: props.renderContext,
				...(extension && getAnalyticsAttributesFromExtension(extension)),
			}) as ForgeUiAnalyticsAttributes,
		[extension, props.renderContext],
	);

	return (
		<EditAnalyticsWrapper attributes={analyticsAttributes}>
			<Box paddingBlockEnd="space.075" xcss={fieldContainerStyles}>
				<ForgeFieldView {...props} />
			</Box>
		</EditAnalyticsWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ForgeLabel = styled.span({
	font: token('font.heading.xsmall'),
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const fieldContainerStyles = xcss({
	width: '100%',
});
